import { useRef, useEffect } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { Banner, Text, Button, VSpacing, useBreakpoint } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import numberFormatter from 'bloko/common/numberFormatter';
import { format } from 'bloko/common/trl';

import translation from 'src/components/translation';
import { useSelectorNonNullable, useSelector } from 'src/hooks/useSelector';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

const TrlKeys = {
    register: 'resumesSearch.inSerp.register',
    benefitsForNotEmptyResult: 'resumesSearch.inSerp.benefits.notEmpty',
    benefitsForEmptyResult: 'resumesSearch.inSerp.benefits.empty',
    registerAction: 'resumesSearch.normalResults.register',
    title: 'search.resume.banner.registration.title',
};

const RegistrationActionForAnonymous: TranslatedComponent = ({ trls }) => {
    const regBannerRef = useRef<HTMLDivElement>(null);
    const registerURL = useSelectorNonNullable((state) => state.authUrl[`auth-employer-url`]);
    const stats = useSelector((state) => state.resumeSearchResult?.stats);

    const { isMobile } = useBreakpoint();

    useEffect(() => {
        if (regBannerRef.current) {
            const { stopSpying } = Analytics.sendHHEventElementShown(regBannerRef.current, {
                name: 'show_registration_anon_action_in_serp',
            });

            return () => {
                stopSpying();
            };
        }

        return undefined;
    }, [regBannerRef]);

    if (stats.normalSearchResults === null || !registerURL) {
        return null;
    }

    const showedAfterRegistration = stats.normalSearchResults - stats.totalUngroupedResults;

    return (
        <div ref={regBannerRef}>
            <Banner
                stretched
                visible
                showBorder
                showClose={false}
                buttonBase={
                    <Button
                        size="small"
                        mode="primary"
                        style="accent"
                        stretched={isMobile}
                        Element={SPALink}
                        to={registerURL}
                        data-qa="resume-serp__action-after-search"
                    >
                        {trls[TrlKeys.registerAction]}
                    </Button>
                }
                title={<Text typography="title-5-semibold">{trls[TrlKeys.title]}</Text>}
                content={
                    <Text typography="paragraph-2-regular">
                        {showedAfterRegistration === 0 && trls[TrlKeys.benefitsForEmptyResult]}
                        {showedAfterRegistration > 0 &&
                            format(trls[TrlKeys.benefitsForNotEmptyResult], {
                                '{0}': numberFormatter.format(`${showedAfterRegistration}`, {
                                    groupSeparator: NON_BREAKING_SPACE,
                                }),
                            })}
                    </Text>
                }
            />
            <VSpacing default={16} />
        </div>
    );
};

export default translation(RegistrationActionForAnonymous);
